import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import CatalogApi from "../api/catalog";
import SpinningLoader from "../components/Common/SpinningLoader";
import { COMMON } from "../constants/localization";

/**
 * Public, same origin stand in for the external api's /GetSiteDocument/{id},
 * served at {siteRoot}/Administration/GetFile/{id} to match the url the legacy
 * Navigator site already serves these documents from.
 *
 * Site documents (the disclosures and the electronic communications agreement)
 * are linked from enrollment, before the user has an account, so the link has to
 * be anonymous. Pointing it straight at the api made every one of those links
 * cross origin. This route keeps them on {siteRoot}, hands the request off to
 * the api and then hands the binary it returns to the browser, so the document
 * still opens in the browser's native viewer exactly as it did before.
 */
const SiteDocument = () => {
  const { id } = useParams<{ id: string }>();
  const [error, setError] = useState(false);

  useEffect(() => {
    // The request outlives the component if the user navigates away mid flight,
    // in which case there is nothing left to hand the document to.
    let abandoned = false;

    const getDocument = async () => {
      try {
        const response = await CatalogApi.GetSiteDocument(id);

        if (abandoned) {
          return;
        }

        // The blob carries the content type the api responded with, so the
        // browser picks the same viewer it would have for the api url.
        // Nothing revokes this url: it dies with this document, which is
        // exactly when the navigation below has finished with it.
        const objectUrl = URL.createObjectURL(response.data);

        // Replace rather than assign so that "back" returns to the page that
        // linked here instead of to an object url that no longer resolves.
        window.location.replace(objectUrl);
      } catch (err) {
        if (!abandoned) {
          setError(true);
        }
      }
    };

    getDocument();

    return () => {
      abandoned = true;
    };
  }, [id]);

  if (error) {
    return <p className="spacing-top-large">{COMMON.ERROR}</p>;
  }

  return <SpinningLoader show={true} />;
};

export default SiteDocument;
